import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, FileSignature, CheckCircle, Clock, Eye } from "lucide-react";

const statusColors = {
  pending_signatures: "bg-amber-50 text-amber-700",
  active: "bg-emerald-50 text-emerald-700",
  expired: "bg-gray-100 text-gray-500",
  terminated: "bg-red-50 text-red-700",
  renewed: "bg-blue-50 text-blue-700",
};

const leaseStatuses = ["pending_signatures", "active", "expired", "terminated", "renewed"];

export default function AdminLeasesPage() {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [genDialog, setGenDialog] = useState(false);
  const [viewLease, setViewLease] = useState(null);
  const [approvedApps, setApprovedApps] = useState([]);
  const [form, setForm] = useState({ application_id: "", start_date: "", end_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [signingId, setSigningId] = useState(null);

  const fetchLeases = (status) => {
    setLoading(true);
    const params = status && status !== "all" ? { status } : {};
    api.get("/admin/leases", { params }).then((r) => setLeases(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeases(tab); }, [tab]);

  const openGenDialog = () => {
    api.get("/admin/applications?status=approved").then((r) => setApprovedApps(r.data)).catch(() => {});
    setGenDialog(true);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.application_id || !form.start_date || !form.end_date) {
      toast.error("Select an application and set both dates");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/leases", form);
      toast.success("Lease generated with payment charges. Tenant notified by email.");
      setGenDialog(false);
      setForm({ application_id: "", start_date: "", end_date: "" });
      fetchLeases(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminSign = async (leaseId) => {
    setSigningId(leaseId);
    try {
      await api.post(`/admin/leases/${leaseId}/sign`);
      toast.success("Lease signed by admin");
      fetchLeases(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSigningId(null);
    }
  };

  const updateStatus = async (leaseId, status) => {
    try {
      await api.put(`/admin/leases/${leaseId}/status`, { status });
      toast.success(`Lease status updated to ${status.replace(/_/g, " ")}`);
      fetchLeases(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Lease Management">
      <div className="space-y-6" data-testid="admin-leases-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto flex-wrap">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="pending_signatures" className="text-xs">Pending</TabsTrigger>
              <TabsTrigger value="active" className="text-xs">Active</TabsTrigger>
              <TabsTrigger value="expired" className="text-xs">Expired</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button className="bg-[#E25822] hover:bg-[#E25822]/90 text-white" onClick={openGenDialog} data-testid="generate-lease-btn">
            <Plus className="h-4 w-4 mr-1" /> Generate Lease
          </Button>
        </div>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : leases.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No leases found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Room</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Signatures</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leases.map((l) => (
                    <TableRow key={l._id} data-testid={`lease-row-${l._id}`}>
                      <TableCell className="font-medium">{l.user_name}</TableCell>
                      <TableCell>{l.room_number}</TableCell>
                      <TableCell className="text-sm">{l.start_date} — {l.end_date}</TableCell>
                      <TableCell>${l.rent_amount}/mo</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${l.tenant_signed_at ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {l.tenant_signed_at ? "Tenant" : "Tenant"}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${l.admin_signed_at ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {l.admin_signed_at ? "Admin" : "Admin"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[l.status]}>{l.status?.replace(/_/g, " ")}</Badge>
                        {l.renewal_requested && <Badge className="bg-blue-50 text-blue-700 ml-1">Renewal</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 items-center">
                          {l.status === "pending_signatures" && !l.admin_signed_at && (
                            <Button
                              size="sm"
                              className="bg-[#0A2540] text-white text-xs h-7"
                              onClick={() => handleAdminSign(l._id)}
                              disabled={signingId === l._id}
                              data-testid={`admin-sign-${l._id}`}
                            >
                              {signingId === l._id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><FileSignature className="h-3 w-3 mr-1" /> Sign</>}
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewLease(l)} data-testid={`view-lease-${l._id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Select onValueChange={(v) => updateStatus(l._id, v)}>
                            <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`lease-status-${l._id}`}>
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              {leaseStatuses.filter((s) => s !== l.status).map((s) => (
                                <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Generate Dialog */}
        <Dialog open={genDialog} onOpenChange={setGenDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="generate-lease-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Generate Lease</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                A full Zimbabwean-compliant lease will be auto-generated with payment charges (deposit + first month rent).
              </p>
            </DialogHeader>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <Label>Approved Application</Label>
                <Select value={form.application_id} onValueChange={(v) => setForm((p) => ({ ...p, application_id: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="lease-app-select">
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedApps.map((a) => (
                      <SelectItem key={a._id} value={a._id}>
                        {a.user_name} — Room {a.room_number}
                      </SelectItem>
                    ))}
                    {approvedApps.length === 0 && <div className="p-2 text-sm text-muted-foreground text-center">No approved applications</div>}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} className="h-11 mt-1.5" required data-testid="lease-start-date" />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} className="h-11 mt-1.5" required data-testid="lease-end-date" />
                </div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                A full student accommodation lease agreement compliant with Zimbabwean law will be auto-generated. Both admin and tenant must sign before the lease activates.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setGenDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#0A2540] text-white" disabled={submitting} data-testid="create-lease-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Lease"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Lease Dialog */}
        <Dialog open={!!viewLease} onOpenChange={(o) => !o && setViewLease(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="view-lease-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Lease — Room {viewLease?.room_number} ({viewLease?.user_name})</DialogTitle>
            </DialogHeader>
            {viewLease && (
              <div className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Badge className={statusColors[viewLease.status]}>{viewLease.status?.replace(/_/g, " ")}</Badge>
                  <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${viewLease.tenant_signed_at ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {viewLease.tenant_signed_at ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    Tenant {viewLease.tenant_signed_at ? `signed ${new Date(viewLease.tenant_signed_at).toLocaleDateString()}` : "not signed"}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${viewLease.admin_signed_at ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {viewLease.admin_signed_at ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    Admin {viewLease.admin_signed_at ? `signed ${new Date(viewLease.admin_signed_at).toLocaleDateString()}` : "not signed"}
                  </span>
                </div>
                <ScrollArea className="h-[450px] border rounded-lg p-4 bg-white">
                  <pre className="text-xs leading-relaxed whitespace-pre-wrap font-body text-[#0F172A]">{viewLease.terms}</pre>
                </ScrollArea>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewLease(null)}>Close</Button>
              {viewLease?.status === "pending_signatures" && !viewLease?.admin_signed_at && (
                <Button className="bg-[#0A2540] text-white" onClick={() => { handleAdminSign(viewLease._id); setViewLease(null); }} data-testid="view-admin-sign-btn">
                  <FileSignature className="h-4 w-4 mr-2" /> Sign as Admin
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
